import React, { useState, useEffect } from "react";
import {
  Box,
  Image,
  IconButton,
  useToast,
  Spinner,
  Flex,
  Text,
  Button,
  HStack,
} from "@chakra-ui/react";
import { CloseIcon, DownloadIcon } from "@chakra-ui/icons";
import axios from "axios";
import { url } from "../crudRequests";

export default function MessagingFilePreview({ preview, setPreview, state }) {
  const [loading, setLoading] = useState(false);
  const [fileContent, setFileContent] = useState(null);
  const [error, setError] = useState(false);
  const toast = useToast();

  const getFileType = (fileName) => {
    const ext = fileName
      .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
      .toLowerCase();
    const fileTypes = {
      image: ["jpg", "png", "jpeg", "gif", "bmp", "svg", "webp"],
      pdf: ["pdf"],
      text: ["txt", "csv", "json", "js", "ts", "html", "css", "py", "xml", "md"],
    };
    return (
      Object.keys(fileTypes).find((key) => fileTypes[key].includes(ext)) ||
      "unsupported"
    );
  };

  const fileType = preview && getFileType(preview?.fileName);

  // Function to fetch file through proxy
  const fetchFileContent = async () => {
    if (!preview || !preview.fileUrl) return;
    
    console.log("=== FILE PREVIEW DEBUG ===");
    console.log("Preview data:", preview);
    console.log("File type:", fileType);
    console.log("State JWT exists?", !!state?.jwt);
    console.log("State org:", state?.organization);
    
    setLoading(true);
    setError(false);
    
    try {
      // For images, we can use the S3 URL directly
      if (fileType === "image") {
        setFileContent(preview.fileUrl);
        setLoading(false);
        return;
      }
      
      // Extract the full path from the S3 URL
      let directoryName = "";
      let fileName = preview.fileName;
      
      // Parse the S3 URL to get the path
      // The URL format is usually: https://bucket.s3.region.amazonaws.com/path/to/file
      // or https://s3.region.amazonaws.com/bucket/path/to/file
      const urlParts = preview.fileUrl.split('.com/');
      if (urlParts.length > 1) {
        // Get everything after .com/
        let fullPath = decodeURIComponent(urlParts[1]);
        
        // If the path includes query parameters, remove them
        const queryIndex = fullPath.indexOf('?');
        if (queryIndex > -1) {
          fullPath = fullPath.substring(0, queryIndex);
        }
        
        // Extract directory and filename from the full path
        const lastSlashIndex = fullPath.lastIndexOf('/');
        if (lastSlashIndex > -1) {
          directoryName = fullPath.substring(0, lastSlashIndex + 1);
          fileName = fullPath.substring(lastSlashIndex + 1);
        }
      } else if (preview.conversationId) {
        // Fallback: use conversationId if URL parsing fails
        directoryName = `messages/${preview.conversationId}/`;
      }
      
      
      // For PDFs and text files, fetch through proxy
      const response = await axios({
        method: "GET",
        url: `${url}/fileProxy`,
        params: {
          s3BucketName: state.organization?.s3BucketName || "brightlighthealth",
          fileName: fileName,
          directoryName: directoryName,
          orgShareName: "brightlighthealth",
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + state.jwt,
        },
        responseType: fileType === "text" ? "text" : "arraybuffer",
      });
      
      if (fileType === "text") {
        setFileContent(response.data);
      } else {
        // Create blob URL for PDFs
        const blob = new Blob([response.data], { 
          type: response.headers["content-type"] || "application/pdf" 
        });
        const blobUrl = URL.createObjectURL(blob);
        setFileContent(blobUrl);
      }
    } catch (error) {
      console.error("Error fetching file:", error);
      console.error("Error details:", error.response?.data);
      setError(true);
      
      // More specific error message
      const errorMessage = error.response?.status === 500 
        ? "Server error while loading file. The file may not exist or there's a server issue."
        : "Unable to load file preview. You can still download the file.";
      
      toast({
        title: "Failed to load preview",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch file content when preview changes
  useEffect(() => {
    if (preview && preview.fileName) {
      if (fileType === "unsupported") {
        toast({
          title: "Unsupported file type",
          description: "This file cannot be previewed. Please download it to view.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setPreview(null);
        return;
      }
      
      // If we already have a blob URL from StaffChatArea, use it directly
      if (preview.isBlob && preview.fileUrl && preview.fileUrl.startsWith("blob:")) {
        console.log("Using provided blob URL directly");
        setFileContent(preview.fileUrl);
        setLoading(false);
      } else {
        // Otherwise fetch the file content
        fetchFileContent();
      }
    }
    
    // Cleanup blob URLs
    return () => {
      if (fileContent && fileContent.startsWith("blob:")) {
        URL.revokeObjectURL(fileContent);
      }
      if (preview?.isBlob && preview?.fileUrl && preview.fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(preview.fileUrl);
      }
    };
  }, [preview]);

  const handleDownload = () => {
    if (preview.fileUrl) {
      // For blob URLs, create a download link
      if (preview.isBlob && preview.fileUrl.startsWith("blob:")) {
        const a = document.createElement("a");
        a.href = preview.fileUrl;
        a.download = preview.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // For regular URLs, open in new tab
        window.open(preview.fileUrl, "_blank");
      }
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Flex h="full" align="center" justify="center">
          <Spinner size="xl" color="gray.400" />
        </Flex>
      );
    }
    
    if (error) {
      return (
        <Flex h="full" align="center" justify="center" direction="column">
          <Text color="gray.500" mb={4}>Unable to load preview</Text>
          <Button
            leftIcon={<DownloadIcon />}
            onClick={handleDownload}
            colorScheme="blue"
            size="sm"
          >
            Download File
          </Button>
        </Flex>
      );
    }
    
    if (!fileContent) return null;
    
    switch (fileType) {
      case "image":
        return (
          <Flex h="full" align="center" justify="center" p={4}>
            <Image 
              src={fileContent} 
              maxH="90vh" 
              maxW="90vw" 
              objectFit="contain"
              alt={preview.fileName}
            />
          </Flex>
        );
      case "pdf":
        return (
          <Box 
            as="iframe" 
            src={fileContent} 
            width="full" 
            height="full"
            border="none"
          />
        );
      case "text":
        return (
          <Box
            as="pre"
            p={4}
            bg="white"
            borderRadius="md"
            overflowY="auto"
            maxH="90vh"
            fontSize="14px"
            fontFamily="monospace"
          >
            {fileContent}
          </Box>
        );
      default:
        return null;
    }
  };

  if (!preview) return null;

  return (
    <Box
      position="fixed"
      w="full"
      h="full"
      bg="rgba(0, 0, 0, 0.8)"
      left={0}
      top={0}
      zIndex={9999}
      onClick={() => setPreview(null)}
    >
      <Box
        position="relative"
        w="full"
        h="full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <HStack
          position="absolute"
          top={0}
          left={0}
          right={0}
          p={4}
          bg="rgba(0, 0, 0, 0.5)"
          justify="space-between"
          zIndex={10}
        >
          <Text color="white" fontSize="16px" fontWeight="500">
            {preview.fileName}
          </Text>
          <HStack spacing={2}>
            <IconButton
              icon={<DownloadIcon />}
              aria-label="Download file"
              size="sm"
              colorScheme="whiteAlpha"
              onClick={handleDownload}
            />
            <IconButton
              icon={<CloseIcon />}
              aria-label="Close preview"
              size="sm"
              colorScheme="whiteAlpha"
              onClick={() => setPreview(null)}
            />
          </HStack>
        </HStack>
        
        {/* Content */}
        <Box h="full" pt="60px" pb={4} px={4}>
          {renderPreview()}
        </Box>
      </Box>
    </Box>
  );
}